import React, { useState, useEffect } from 'react';
import { Snackbar, Paper, Typography, Box, Stack, Button, useTheme, alpha } from '@mui/material';
import { CookieIcon } from 'lucide-react';
import AppUtils from '../utils/app-utils';
import type { ConsentSettings } from "../types/consent.tsx";

declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

const CookieConsent: React.FC = () => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    
    useEffect(() => {
        // Initialize Google's consent mode with default settings
        window.gtag('consent', 'default', {
            'analytics_storage': 'denied',
            'ad_storage': 'denied',
            'functionality_storage': 'denied',
            'personalization_storage': 'denied',
            'security_storage': 'granted'
        });

        // Check if user has already made a choice
        const consentCookie = AppUtils.getAllowCookiesCookie();
        if (!consentCookie) {
            setOpen(true);
        } else {
            // Update consent state based on saved preferences
            updateConsentState(consentCookie);
        }
    }, []);

    const updateConsentState = (preferences: ConsentSettings) => {
        // Update Google's consent mode
        window.gtag('consent', 'update', {
            'analytics_storage': preferences.analytics_storage,
            'ad_storage': preferences.ad_storage,
            'functionality_storage': preferences.functionality_storage,
            'personalization_storage': preferences.personalization_storage,
            'security_storage': preferences.security_storage
        });
    };

    const handleAcceptAll = () => {
        const preferences: ConsentSettings = {
            analytics_storage: 'granted',
            ad_storage: 'granted',
            functionality_storage: 'granted',
            personalization_storage: 'granted',
            security_storage: 'granted',
            moes_random_value: AppUtils.getRandomMoesValue(),
        };

        AppUtils.saveAllowCookiesCookie(preferences);

        updateConsentState(preferences);
        setOpen(false);
    };

    const handleEssentialOnly = () => {
        const preferences: ConsentSettings = {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            functionality_storage: 'denied',
            personalization_storage: 'denied',
            security_storage: 'granted',
            moes_random_value: AppUtils.getRandomMoesValue(),
        };

        AppUtils.saveAllowCookiesCookie(preferences);

        updateConsentState(preferences);
        setOpen(false);
    };

    return (
        <Snackbar open={open} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} sx={{ mb: 3, mx: 2 }}>
            <Paper
                elevation={6}
                sx={{
                    p: 3,
                    maxWidth: '600px',
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <CookieIcon size={24} color={theme.palette.primary.main} />
                    <Typography variant="h6"><s>FREE BEERS🍻</s> COOKIES🍪!</Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    We use cookies to enhance your browsing (moes'ing) experience and analyze our traffic.
                    We also use Google Analytics to understand what visitors do on our site.
                    Select your preferences below:
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Essential (Always Active)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Necessary for the website to function properly.
                    </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Analytics & Performance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Help us understand how visitors interact with our website.
                    </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        onClick={handleEssentialOnly}
                        sx={{ flex: { xs: '1', sm: '0 auto' } }}
                    >
                        Essential Only
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAcceptAll}
                        sx={{ flex: { xs: '1', sm: '0 auto' } }}
                    >
                        Accept All
                    </Button>
                </Stack>
            </Paper>
        </Snackbar>
    );
};

export default CookieConsent;